import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TablePagination,
    MenuItem,
    Chip
} from "@mui/material";

import { Delete, Edit, PersonAdd } from "@mui/icons-material";

import {
    collection,
    addDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    doc,
    orderBy,
    query
} from "firebase/firestore";

import { db } from "../../firebase";

function Staffmgmt() {
    const [staffList, setStaffList] = useState([]);
    const [open, setOpen] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const [selectedId, setSelectedId] = useState(null);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const rowsPerPage = 50;

    const [formData, setFormData] = useState({
        staffCode: "",
        name: "",
        mobile: "",
        address: "",
        payment: "",
        post: "",
        paidHolidays: 3,       // Added: defaults to 3 days
        status: "Currently Working", // Added: working status
    });

    useEffect(() => {
        loadStaff();
    }, []);

    const loadStaff = async () => {
        try {
            const staffQuery = query(
                collection(db, "staff"),
                orderBy('staffCode', 'asc')
            );

            const snapshot = await getDocs(staffQuery);
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setStaffList(data);
        } catch (error) {
            console.error("Error loading staff:", error);
        }
    };

    const generateStaffCode = () => {
        if (staffList.length === 0) return "MIS-01";

        const maxId = Math.max(
            ...staffList.map((staff) => {
                const code = staff.staffCode || "";
                const numericPart = code.split("-")[1];
                return numericPart ? parseInt(numericPart, 10) : 0;
            })
        );

        const nextId = maxId + 1;
        return `MIS-${nextId.toString().padStart(2, "0")}`;
    };

    const handleOpenAdd = () => {
        setFormData({
            staffCode: generateStaffCode(),
            name: "",
            mobile: "",
            address: "",
            payment: "",
            post: "",
            paidHolidays: 3, 
            status: "Currently Working"
        });

        setSelectedId(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async () => {
        if (selectedId) {
            await updateDoc(doc(db, "staff", selectedId), formData);
        } else {
            await addDoc(collection(db, "staff"), formData);
        }

        setOpen(false);
        loadStaff();
    };

    const handleEdit = (staff) => {
        setFormData(staff);
        setSelectedId(staff.id);
        setOpen(true);
    };

    const handleDeleteConfirm = (id) => {
        setSelectedId(id);
        setDeleteDialog(true);
    };

    const handleDelete = async () => {
        await deleteDoc(doc(db, "staff", selectedId));
        setDeleteDialog(false);
        loadStaff();
    };

    const filteredStaff = staffList.filter((staff) =>
        staff.name?.toLowerCase().includes(search.toLowerCase()) ||
        staff.post?.toLowerCase().includes(search.toLowerCase()) ||
        staff.mobile?.includes(search)
    );

    return (
        <Box p={1}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography variant="h5" fontWeight="bold">
                    Staff Management
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={handleOpenAdd}
                >
                    Add New Staff
                </Button>
            </Box>

            <TextField
                label="Search Staff (Name, Post, or Mobile)"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                }}
            />

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ background: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Staff Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Post</TableCell>
                            <TableCell>Mobile</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>Salary</TableCell>
                            <TableCell>Allow Holidays</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredStaff.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((staff, index) => (
                            <TableRow key={staff.id} hover>
                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                <TableCell>{staff.staffCode}</TableCell>
                                <TableCell>{staff.name}</TableCell>
                                <TableCell>{staff.post}</TableCell>
                                <TableCell>{staff.mobile}</TableCell>
                                <TableCell>{staff.address}</TableCell>
                                <TableCell>₹{staff.payment}</TableCell>
                                <TableCell>{staff.paidHolidays}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={staff.status || "Currently Working"} 
                                        color={staff.status === "Not Working" ? "error" : "success"}
                                        size="small" 
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton color="primary" size="small" onClick={() => handleEdit(staff)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton color="error" size="small" onClick={() => handleDeleteConfirm(staff.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={filteredStaff.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                />
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={open} fullWidth maxWidth="md">
                <DialogTitle>
                    {selectedId ? "Edit Staff" : "Add Staff"}
                </DialogTitle>

                <DialogContent dividers>
                    <Grid className="row" spacing={2} mt={1}>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Staff Code"
                                fullWidth
                                size="small"
                                value={formData.staffCode}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Name"
                                fullWidth
                                size="small"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Post"
                                fullWidth
                                size="small"
                                value={formData.post}
                                onChange={(e) => setFormData({ ...formData, post: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Mobile"
                                fullWidth
                                size="small"
                                value={formData.mobile}
                                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Salary"
                                type="number"
                                fullWidth
                                size="small"
                                value={formData.payment}
                                onChange={(e) => setFormData({ ...formData, payment: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField
                                label="Paid Holidays (Per Month)"
                                type="number"
                                fullWidth
                                size="small"
                                value={formData.paidHolidays}
                                onChange={(e) => setFormData({ ...formData, paidHolidays: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                            <TextField
                                label="Address"
                                fullWidth
                                size="small"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                            <TextField
                                select
                                label="Status"
                                fullWidth
                                size="small"
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            >
                                <MenuItem value="Currently Working">Currently Working</MenuItem>
                                <MenuItem value="Not Working">Not Working</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this staff member?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Staffmgmt;